import React, { useEffect, useRef, useState } from "react";

/*
	Cámara lectora de códigos (barras y QR), sacada del escáner de Bodegas (src/navigation/v2/store/warehouses/escaner.tsx)
	el 2026-10-22 para que la Entrada de Eventos use el mismo lector. Un solo stream compartido por página y el bip de
	confirmación viven aquí; cada pantalla decide qué hacer con los códigos.
*/
const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "code_93", "itf", "qr_code"];
// The same formats for ZXing (iPhone): fewer formats to try = faster reads.
const ZXING_FORMATS = ["EAN_13", "EAN_8", "UPC_A", "UPC_E", "CODE_128", "CODE_39", "CODE_93", "ITF", "QR_CODE"];

/*
	One camera stream for the whole page (Christy, iPhone, 2026-10-19): iOS Safari asks for the camera permission again every
	time getUserMedia runs after the tracks were stopped. Pausing the scanner (photo, alta, search by name) only detaches the
	<video> and stops decoding; the tracks stop CAMERA_RELEASE_MS after the last use (closing the scanner included), so the
	camera light never stays on for good. If iOS ended the tracks itself (the native camera of a photo input does that), a new
	stream is requested.
*/
const CAMERA_RELEASE_MS = 3 * 60 * 1000;
let sharedStream: MediaStream | null = null;
let openingStream: Promise<MediaStream> | null = null;
let releaseTimer: any = null;

const isLive = (stream: MediaStream | null): stream is MediaStream => !!stream && stream.getVideoTracks().some((track) => track.readyState === "live");

const stopSharedStream = () => {
	if (sharedStream) sharedStream.getTracks().forEach((track) => track.stop());
	sharedStream = null;
};

const openCamera = async (): Promise<MediaStream> => {
	if (releaseTimer) { clearTimeout(releaseTimer); releaseTimer = null; }
	if (isLive(sharedStream)) return sharedStream;
	stopSharedStream();
	if (!openingStream) {
		openingStream = navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
			.then((stream) => { sharedStream = stream; return stream; })
			.finally(() => { openingStream = null; });
	}
	return openingStream;
};

const releaseCameraLater = () => {
	if (releaseTimer) clearTimeout(releaseTimer);
	releaseTimer = setTimeout(() => { releaseTimer = null; stopSharedStream(); }, CAMERA_RELEASE_MS);
};

/*
	iPhone has no vibration: a short beep (WebAudio, unlocked by the first touch on the scanner) and a green flash of the frame
	confirm each piece, so nobody scans again "because it didn't sound".
*/
let audioContext: AudioContext | null = null;
const unlockAudio = () => {
	try {
		const Context = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!Context) return;
		if (!audioContext) audioContext = new Context();
		if (audioContext && audioContext.state === "suspended") audioContext.resume().catch(() => undefined);
	} catch { /* sin audio */ }
};
const beep = () => {
	try {
		if (!audioContext || audioContext.state !== "running") return;
		const oscillator = audioContext.createOscillator();
		const gain = audioContext.createGain();
		oscillator.frequency.value = 1800;
		gain.gain.value = 0.08;
		oscillator.connect(gain);
		gain.connect(audioContext.destination);
		oscillator.start();
		oscillator.stop(audioContext.currentTime + 0.08);
	} catch { /* sin audio */ }
};

type CameraState = "starting" | "on" | "denied" | "unsupported";

/** Prende la cámara trasera y avisa los códigos que ve en cada cuadro. Se pausa al desactivarse (para tomar fotos o al cerrar). */
const useBarcodeCamera = (videoRef: React.RefObject<HTMLVideoElement>, enabled: boolean, onCodes: (codes: string[]) => void): CameraState => {
	const [state, setState] = useState<CameraState>("starting");
	const callback = useRef(onCodes);
	callback.current = onCodes;
	useEffect(() => {
		if (!enabled) return;
		let stopped = false;
		let timer: any = null;
		let controls: { stop: () => void } | null = null;
		const videoElement = videoRef.current;
		setState("starting");
		const start = async () => {
			if (!navigator.mediaDevices?.getUserMedia || !videoRef.current) { setState("unsupported"); return; }
			const Native = (window as any).BarcodeDetector;
			let detector: any = null;
			if (Native) {
				try {
					const supported: string[] = await Native.getSupportedFormats();
					const formats = FORMATS.filter((format) => supported.includes(format));
					if (formats.length > 0) detector = new Native({ formats });
				} catch { detector = null; }
			}
			if (stopped) return;
			try {
				const stream = await openCamera();
				const video = videoRef.current;
				// Closed while the camera was opening: arm the delayed release again (openCamera cleared it).
				if (stopped || !video) { releaseCameraLater(); return; }
				video.srcObject = stream;
				await video.play();
				if (stopped) return;
				if (detector) {
					setState("on");
					const tick = async () => {
						if (stopped) return;
						try {
							if (video.readyState >= 2) {
								const found = await detector.detect(video);
								if (stopped) return;
								// Every code in the frame (a box can carry an EAN and a QR): the screen decides which one counts.
								const codes = (found || []).map((entry: any) => String(entry.rawValue || "").trim()).filter(Boolean);
								if (codes.length > 0) callback.current(codes);
							}
						} catch { /* un cuadro que no se pudo leer */ }
						timer = setTimeout(tick, 200);
					};
					tick();
				} else {
					const [{ BrowserMultiFormatReader }, library] = await Promise.all([import("@zxing/browser"), import("@zxing/library")]);
					if (stopped || !videoRef.current) return;
					const hints = new Map<any, any>();
					hints.set(library.DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS.map((format) => (library.BarcodeFormat as any)[format]));
					const reader = new BrowserMultiFormatReader(hints);
					// Decodes from the video that already shows the shared stream: ZXing never opens (or stops) the camera itself.
					const scanning = await reader.decodeFromVideoElement(video, (result) => {
						if (result && !stopped) callback.current([result.getText().trim()]);
					});
					if (stopped) { scanning.stop(); return; }
					controls = scanning;
					setState("on");
				}
			} catch (error: any) {
				if (stopped) return;
				setState(error?.name === "NotAllowedError" || error?.name === "SecurityError" ? "denied" : "unsupported");
			}
		};
		start();
		return () => {
			stopped = true;
			if (timer) clearTimeout(timer);
			if (controls) controls.stop();
			if (videoElement) videoElement.srcObject = null;
			releaseCameraLater();
		};
	}, [enabled]);
	return state;
};

export { FORMATS, ZXING_FORMATS, unlockAudio, beep, useBarcodeCamera };
export type { CameraState };
